/**
 * A pasted HTML fragment read as a tree (table H in the part 10 log, H5, H7–H9). The page's Content Security Policy
 * allows no Trusted Types policy, so `DOMParser` can't be used, and the fragment is never rendered: this reader only
 * finds elements, their attributes and their text, which `rich-paste.ts` turns into source through an allowlist. It
 * never throws and runs in linear time; whatever it misreads becomes text, never markup the allowlist didn't make.
 */
import { decodeEntity } from '@cockatrice/core';

/** An element: its lowercase name, its attributes (lowercase names, decoded values) and its children. */
export interface HtmlElement {
  readonly kind: 'element';
  readonly name: string;
  readonly attributes: ReadonlyMap<string, string>;
  readonly children: HtmlNode[];
}

/** Text, references decoded. */
export interface HtmlText {
  readonly kind: 'text';
  readonly text: string;
}

/** A node of a fragment's tree. */
export type HtmlNode = HtmlElement | HtmlText;

/** H8: deeper elements count as their text. */
export const MAX_DEPTH = 64;

/** Elements with no content or end tag. */
const VOID: ReadonlySet<string> = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr']);
/** Elements whose content is raw text up to their end tag. */
const RAW_TEXT: ReadonlySet<string> = new Set(['script', 'style', 'textarea', 'title', 'xmp', 'iframe', 'noembed', 'noframes', 'noscript', 'plaintext']);
/** Elements a `p` can't hold: starting one closes an open `p`. */
const CLOSES_P: ReadonlySet<string> = new Set([
  'address', 'article', 'aside', 'blockquote', 'div', 'dl', 'fieldset', 'figure', 'footer', 'form', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'header', 'hr', 'li', 'main', 'nav', 'ol', 'p', 'pre', 'section', 'table', 'ul',
]);

const NAME_START = /[a-z]/iu;
const SPACE = /[\t\n\f\r ]/u;

/** `&name;`, `&#n;` and `&#xh;` decoded (H7); anything else, `&` included, as it is. */
export function decodeReferences(text: string): string {
  if (!text.includes('&')) return text;
  return text.replace(/&(?:#\d{1,8}|#x[\da-f]{1,7}|[a-z][a-z\d]{0,31});/giu, (reference) => decodeEntity(reference) ?? reference);
}

/** Reads one tag's attributes from `at`, up to its `>`; returns them, whether it closes itself, and where it ends. */
function readAttributes(html: string, at: number): { attributes: Map<string, string>; selfClosing: boolean; end: number } {
  const attributes = new Map<string, string>();
  let i = at;
  while (i < html.length) {
    const char = html.charAt(i);
    if (char === '>') return { attributes, selfClosing: html.charAt(i - 1) === '/', end: i + 1 };
    if (SPACE.test(char) || char === '/') {
      i += 1;
      continue;
    }
    let nameEnd = i;
    while (nameEnd < html.length && !SPACE.test(html.charAt(nameEnd)) && !'/>='.includes(html.charAt(nameEnd))) nameEnd += 1;
    if (nameEnd === i) nameEnd = i + 1;
    const name = html.slice(i, nameEnd).toLowerCase();
    i = nameEnd;
    while (i < html.length && SPACE.test(html.charAt(i))) i += 1;
    let value = '';
    if (html.charAt(i) === '=') {
      i += 1;
      while (i < html.length && SPACE.test(html.charAt(i))) i += 1;
      const quote = html.charAt(i);
      if (quote === '"' || quote === "'") {
        const close = html.indexOf(quote, i + 1);
        const valueEnd = close < 0 ? html.length : close;
        value = html.slice(i + 1, valueEnd);
        i = valueEnd + 1;
      } else {
        let valueEnd = i;
        while (valueEnd < html.length && !SPACE.test(html.charAt(valueEnd)) && html.charAt(valueEnd) !== '>') valueEnd += 1;
        value = html.slice(i, valueEnd);
        i = valueEnd;
      }
    }
    if (!attributes.has(name)) attributes.set(name, decodeReferences(value));
  }
  return { attributes, selfClosing: false, end: html.length };
}

/**
 * Where `</name` next starts in `html` from `from`, its name in any case; -1 for none. Searched in `html` itself: a
 * lowercased copy can be longer (`İ` lowercases to two units), and its indices would land past the tag (*deep-fix r10*).
 */
function endTagAt(html: string, name: string, from: number): number {
  for (let at = html.indexOf('</', from); at !== -1; at = html.indexOf('</', at + 2)) {
    if (html.slice(at + 2, at + 2 + name.length).toLowerCase() === name) return at;
  }
  return -1;
}

/** Reads `html` into a tree of elements and text (H5–H9). Never throws; linear in the input. */
export function readHtml(html: string): HtmlElement {
  const root: HtmlElement = { kind: 'element', name: '#root', attributes: new Map(), children: [] };
  /** The open elements, innermost first: each with the one it is in, and how deep it is. */
  interface Frame {
    readonly element: HtmlElement;
    readonly parent: Frame | null;
    readonly depth: number;
  }
  let open: Frame = { element: root, parent: null, depth: 0 };
  const addText = (text: string): void => {
    if (text === '') return;
    const children = open.element.children;
    const last = children.at(-1);
    if (last?.kind === 'text') children[children.length - 1] = { kind: 'text', text: last.text + text };
    else children.push({ kind: 'text', text });
  };
  /** Closes the innermost open element named `name`, and every element inside it; none when none is open. */
  const close = (name: string): void => {
    let frame = open;
    while (frame.parent !== null) {
      if (frame.element.name === name) {
        open = frame.parent;
        return;
      }
      frame = frame.parent;
    }
  };
  let i = 0;
  let textStart = 0;
  const flush = (end: number): void => {
    addText(decodeReferences(html.slice(textStart, end)));
  };
  while (i < html.length) {
    const lt = html.indexOf('<', i);
    if (lt < 0) break;
    const next = html.charAt(lt + 1);
    if (html.startsWith('<!--', lt)) {
      flush(lt);
      const end = html.indexOf('-->', lt + 4);
      i = end < 0 ? html.length : end + 3;
      textStart = i;
    } else if (next === '!' || next === '?') {
      flush(lt);
      const end = html.indexOf('>', lt + 2);
      i = end < 0 ? html.length : end + 1;
      textStart = i;
    } else if (next === '/' && NAME_START.test(html.charAt(lt + 2))) {
      flush(lt);
      let nameEnd = lt + 2;
      while (nameEnd < html.length && !SPACE.test(html.charAt(nameEnd)) && !'/>'.includes(html.charAt(nameEnd))) nameEnd += 1;
      const end = html.indexOf('>', nameEnd);
      close(html.slice(lt + 2, nameEnd).toLowerCase());
      i = end < 0 ? html.length : end + 1;
      textStart = i;
    } else if (NAME_START.test(next)) {
      flush(lt);
      let nameEnd = lt + 1;
      while (nameEnd < html.length && !SPACE.test(html.charAt(nameEnd)) && !'/>'.includes(html.charAt(nameEnd))) nameEnd += 1;
      const name = html.slice(lt + 1, nameEnd).toLowerCase();
      const tag = readAttributes(html, nameEnd);
      i = tag.end;
      textStart = i;
      if (RAW_TEXT.has(name)) {
        // Raw text runs to the element's end tag; it is kept (whether it counts is the converter's choice, H5).
        const endTag = endTagAt(html, name, i);
        const contentEnd = endTag < 0 ? html.length : endTag;
        const element: HtmlElement = { kind: 'element', name, attributes: tag.attributes, children: [] };
        if (contentEnd > i) element.children.push({ kind: 'text', text: html.slice(i, contentEnd) });
        open.element.children.push(element);
        const after = endTag < 0 ? -1 : html.indexOf('>', endTag);
        i = after < 0 ? html.length : after + 1;
        textStart = i;
        continue;
      }
      if (CLOSES_P.has(name)) close('p');
      if (name === 'li') {
        // An item closes the open item of its own list.
        let frame = open;
        while (frame.parent !== null && frame.element.name !== 'ul' && frame.element.name !== 'ol') {
          if (frame.element.name === 'li') {
            open = frame.parent;
            break;
          }
          frame = frame.parent;
        }
      }
      const element: HtmlElement = { kind: 'element', name, attributes: tag.attributes, children: [] };
      open.element.children.push(element);
      if (!VOID.has(name) && !tag.selfClosing && open.depth < MAX_DEPTH) open = { element, parent: open, depth: open.depth + 1 };
    } else {
      i = lt + 1;
    }
  }
  flush(html.length);
  return root;
}
